import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import type {
  League, LeagueMember, LineupSlot, NflTeam, Unit, UnitBoardEntry, Week, SlotCode,
} from '../lib/types'

interface Game {
  id: string
  home_team: string
  away_team: string
  kickoff_at: string
}

export function useBoard(leagueId: string | undefined) {
  const [league, setLeague] = useState<League | null>(null)
  const [me, setMe] = useState<LeagueMember | null>(null)
  const [weeks, setWeeks] = useState<Week[]>([])
  const [weekId, setWeekId] = useState<string | null>(null)
  const [teams, setTeams] = useState<Record<string, NflTeam>>({})
  const [units, setUnits] = useState<Unit[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [burns, setBurns] = useState<Record<string, number | null>>({})
  const [myPicks, setMyPicks] = useState<LineupSlot[]>([])
  const [avgByUnit, setAvgByUnit] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // static-ish data
  useEffect(() => {
    if (!leagueId) return
    ;(async () => {
      const [{ data: lg }, { data: tm }, { data: un }, { data: wk }, { data: user }] =
        await Promise.all([
          supabase.from('leagues').select('*').eq('id', leagueId).single(),
          supabase.from('nfl_teams').select('*'),
          supabase.from('units').select('*'),
          supabase.from('weeks').select('*').order('phase').order('week_no'),
          supabase.auth.getUser(),
        ])

      if (lg) setLeague(lg as League)
      setTeams(Object.fromEntries((tm ?? []).map((t: NflTeam) => [t.abbr, t])))
      setUnits((un ?? []) as Unit[])
      // a league may play an abbreviated regular season; playoffs always run
      const all = (wk ?? []) as Week[]
      const inWindow = lg
        ? all.filter((w) =>
            w.phase === 'playoff' ||
            (w.week_no >= (lg as League).start_week && w.week_no <= (lg as League).end_week))
        : all
      setWeeks(inWindow)

      const uid = user.user?.id
      if (uid) {
        const { data: mem } = await supabase
          .from('league_members')
          .select('*')
          .eq('league_id', leagueId)
          .eq('user_id', uid)
          .maybeSingle()
        setMe((mem ?? null) as LeagueMember | null)
      }

      const { data: season } = await supabase
        .from('seasons').select('*').eq('is_active', true).maybeSingle()
      const current = inWindow.find(
        (w) => w.week_no === season?.current_week && w.phase === season?.current_phase,
      )
      setWeekId(current?.id ?? inWindow[0]?.id ?? null)
      setLoading(false)
    })()
  }, [leagueId])

  const refreshWeek = useCallback(async () => {
    if (!leagueId || !weekId || !me) return
    const phase = weeks.find((w) => w.id === weekId)?.phase ?? 'regular'
    const [{ data: gm }, { data: bg }, { data: picks }, { data: scores }] = await Promise.all([
      supabase.from('games').select('id,home_team,away_team,kickoff_at').eq('week_id', weekId),
      // Burns are phase-scoped in the regular season and WEEK-scoped in the
      // playoffs, matching the two partial unique indexes on lineup_slots.
      //
      // The playoff field shrinks to two teams by the final, so a burn that
      // carried across rounds left exactly two QB rooms in existence and a
      // manager who had spent both with no legal lineup at all. Simulated,
      // that hit 100% of managers who simply took the best team available.
      // Playoffs are now a clean board every round.
      //
      // This must agree with the database. If the client thinks a room is
      // burned and the database does not, the room is greyed out and nobody
      // can tell you why.
      (phase === 'playoff'
        ? supabase.from('v_burn_grid').select('unit_id,burned_week_no,is_burned')
            .eq('league_id', leagueId).eq('member_id', me.id)
            .eq('phase', phase).eq('is_burned', true).eq('burned_week_id', weekId)
        : supabase.from('v_burn_grid').select('unit_id,burned_week_no,is_burned')
            .eq('league_id', leagueId).eq('member_id', me.id)
            .eq('phase', phase).eq('is_burned', true)),
      supabase.from('lineup_slots').select('*')
        .eq('league_id', leagueId).eq('member_id', me.id).eq('week_id', weekId),
      supabase.from('unit_week_scores').select('unit_id,points')
        .eq('profile_id', league?.profile_id ?? 'half_ppr'),
    ])

    setGames((gm ?? []) as Game[])
    setBurns(Object.fromEntries((bg ?? []).map((b: any) => [b.unit_id, b.burned_week_no])))
    setMyPicks((picks ?? []) as LineupSlot[])

    const totals: Record<string, { sum: number; n: number }> = {}
    for (const s of (scores ?? []) as any[]) {
      totals[s.unit_id] ??= { sum: 0, n: 0 }
      totals[s.unit_id].sum += Number(s.points)
      totals[s.unit_id].n += 1
    }
    setAvgByUnit(
      Object.fromEntries(
        Object.entries(totals).map(([k, v]) => [k, v.n ? v.sum / v.n : 0]),
      ),
    )
  }, [leagueId, weekId, me, league?.profile_id, weeks])

  useEffect(() => { void refreshWeek() }, [refreshWeek])

  const board: UnitBoardEntry[] = useMemo(() => {
    const now = Date.now()
    const byTeam = new Map<string, Game>()
    for (const g of games) {
      byTeam.set(g.home_team, g)
      byTeam.set(g.away_team, g)
    }
    return units.map((u) => {
      const g = byTeam.get(u.team)
      const isHome = g?.home_team === u.team
      const kickoff = g?.kickoff_at ?? null
      return {
        unit: u,
        team: teams[u.team],
        opponent: g ? (isHome ? g.away_team : g.home_team) : null,
        isHome: !!isHome,
        kickoffAt: kickoff,
        onBye: !g,
        locked: !!kickoff && new Date(kickoff).getTime() <= now,
        burned: u.id in burns,
        burnedWeekNo: burns[u.id] ?? null,
        projected: null,
        seasonAvg: avgByUnit[u.id] ?? null,
      }
    })
  }, [units, teams, games, burns, avgByUnit])

  const setPick = useCallback(
    async (slot: SlotCode, unitId: string) => {
      if (!leagueId || !weekId) return
      setError(null)
      const { error } = await supabase.rpc('set_pick', {
        p_league_id: leagueId,
        p_week_id: weekId,
        p_slot: slot,
        p_unit_id: unitId,
      })
      if (error) setError(error.message)
      await refreshWeek()
    },
    [leagueId, weekId, refreshWeek],
  )

  const clearPick = useCallback(
    async (slot: SlotCode) => {
      if (!leagueId || !weekId) return
      setError(null)
      const { error } = await supabase.rpc('clear_pick', {
        p_league_id: leagueId,
        p_week_id: weekId,
        p_slot: slot,
      })
      if (error) setError(error.message)
      await refreshWeek()
    },
    [leagueId, weekId, refreshWeek],
  )

  return {
    league, me, weeks, weekId, setWeekId, board, myPicks,
    loading, error, setPick, clearPick, refreshWeek,
  }
}
